import { useEffect, useRef, useState } from 'react';

export default function Home() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // カウントダウン
  const TARGET_DATE = new Date('2026-03-11T08:00:00+09:00');
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = TARGET_DATE.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, launched: true };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      launched: false,
    };
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const diff = TARGET_DATE.getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, launched: true });
        clearInterval(timer);
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        launched: false,
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll('.fade-up').forEach((el) => {
      observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden" style={{ fontFamily: "'Noto Sans JP', sans-serif", background: '#f8faf5' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@300;400;700&family=Noto+Sans+JP:wght@300;400;500;700;900&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&display=swap');

        * { box-sizing: border-box; }

        :root {
          --green: #c8dbb8;
          --green-deep: #7aaa5a;
          --green-text: #4a7a2a;
          --yellow: #e8dfa0;
          --yellow-deep: #c8b840;
          --yellow-text: #7a6a10;
          --sage: #d4e0c8;
          --sage-deep: #8aaa78;
          --sage-text: #3a6a2a;
          --beige: #f0ede0;
          --beige-deep: #c8c0a0;
          --beige-text: #7a7050;
          --dark: #2a3020;
          --cream: #f8faf5;
          --milk: #f4f7f0;
        }

        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gentlePulse {
          0%,100% { box-shadow: 0 4px 24px rgba(122,170,90,0.25); }
          50% { box-shadow: 0 8px 36px rgba(122,170,90,0.45); }
        }
        @keyframes slideRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .fade-up { opacity:0; transform:translateY(28px); transition:opacity 0.8s cubic-bezier(.22,1,.36,1), transform 0.8s cubic-bezier(.22,1,.36,1); }
        .fade-up.is-visible { opacity:1; transform:translateY(0); }
        .float-anim { animation: float 4s ease-in-out infinite; }
        .hero-animate { animation: fadeSlideIn 1s cubic-bezier(.22,1,.36,1) both; }
        .hero-animate-delay { animation: fadeSlideIn 1s cubic-bezier(.22,1,.36,1) 0.2s both; }
        .hero-animate-delay2 { animation: fadeSlideIn 1s cubic-bezier(.22,1,.36,1) 0.4s both; }
        .hero-animate-delay3 { animation: fadeSlideIn 1s cubic-bezier(.22,1,.36,1) 0.6s both; }

        /* ===== CTA LINE BUTTON ===== */
        .line-btn {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: linear-gradient(135deg, #ff8c42 0%, #f97316 50%, #fb923c 100%);
          color: #fff;
          font-weight: 900;
          padding: 18px 28px;
          border-radius: 9999px;
          font-size: 14px;
          width: 100%;
          cursor: pointer;
          text-decoration: none;
          letter-spacing: 0.04em;
          overflow: hidden;
          transition: transform 0.35s cubic-bezier(.22,1,.36,1), box-shadow 0.35s cubic-bezier(.22,1,.36,1);
          box-shadow: 0 4px 20px rgba(249,115,22,0.40), 0 1px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.22);
          white-space: nowrap;
          animation: btnGlow 2.8s ease-in-out infinite;
        }

        @keyframes btnGlow {
          0%, 100% {
            box-shadow: 0 4px 20px rgba(249,115,22,0.40), 0 1px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.22);
            transform: translateY(0px);
          }
          50% {
            box-shadow: 0 10px 38px rgba(249,115,22,0.62), 0 2px 8px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.28);
            transform: translateY(-3px);
          }
        }

        .line-btn::before {
          content: '';
          position: absolute;
          top: 0; left: -60%;
          width: 45%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
          transform: skewX(-18deg);
          animation: btnShimmer 3.5s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }

        @keyframes btnShimmer {
          0%   { left: -60%; opacity: 0; }
          10%  { opacity: 1; }
          50%  { left: 120%; opacity: 1; }
          55%  { opacity: 0; }
          100% { left: 120%; opacity: 0; }
        }

        .line-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background: linear-gradient(160deg, rgba(255,255,255,0.18) 0%, transparent 55%);
          pointer-events: none;
          z-index: 1;
        }

        .line-btn span, .line-btn i {
          position: relative;
          z-index: 3;
        }

        .line-btn:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 16px 48px rgba(249,115,22,0.58), 0 2px 8px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.28);
          animation: none;
        }

        .line-btn:active {
          transform: scale(0.97) translateY(0px);
          box-shadow: 0 4px 14px rgba(249,115,22,0.28);
          transition: transform 0.12s, box-shadow 0.12s;
        }

        .cta-wrap {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .shimmer-text {
          color: var(--green-text);
          font-weight: 900;
        }

        .glass-card {
          background: rgba(255,255,255,0.78);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.9);
          box-shadow: 0 4px 24px rgba(120,140,80,0.08);
        }

        .section-eyebrow {
          font-size:10px; letter-spacing:0.22em; color: var(--green-deep);
          font-weight:600; text-transform:uppercase; text-align:center;
          display:block; margin-bottom:10px;
          font-family:'Cormorant Garamond', serif;
        }

        .serif-heading { font-family:'Noto Serif JP', serif; }

        .worry-card {
          background:rgba(255,255,255,0.92);
          border:1px solid rgba(138,170,90,0.25);
          border-radius:16px;
          padding:10px 13px;
          font-size:11px;
          font-weight:700;
          color: var(--dark);
          line-height:1.6;
          box-shadow: 0 2px 12px rgba(100,140,80,0.08);
        }

        body { background:#f8faf5; overflow-x:hidden; }
      `}</style>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden min-h-[100svh] flex flex-col w-full">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/0857631659ef80cc289aceb112b489a0.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        ></div>
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(170deg, rgba(30,45,20,0.52) 0%, rgba(30,45,20,0.22) 38%, rgba(248,250,255,0.0) 62%, rgba(248,250,255,1) 100%)',
          }}
        ></div>

        <div className="relative z-10 max-w-sm mx-auto w-full px-6 pt-14 pb-0 flex flex-col items-center text-center flex-1">
          <div className="hero-animate mb-6">
            {timeLeft.launched ? (
              <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.4)', borderRadius:'9999px', padding:'6px 14px', fontSize:'10px', fontWeight:700, color:'rgba(255,255,255,0.92)', letterSpacing:'0.12em', textTransform:'uppercase' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#f9d71c] inline-block"></span>
                CAMPFIRE にて公開中！
              </span>
            ) : (
              <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(255,255,255,0.25)', border:'1px solid rgba(255,255,255,0.5)', borderRadius:'9999px', padding:'5px 14px', fontSize:'10px', fontWeight:700, color:'#fff', letterSpacing:'0.12em', textTransform:'uppercase' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#f9d71c] inline-block"></span>
                  3月11日 8:00 CAMPFIRE にて公開予定
                </div>
                <div style={{ display:'inline-flex', alignItems:'center', gap:'4px', background:'rgba(255,255,255,0.88)', border:'1px solid rgba(255,255,255,0.95)', borderRadius:'14px', padding:'9px 16px', boxShadow:'0 3px 16px rgba(0,0,0,0.10)' }}>
                  {[
                    { value: timeLeft.days, label: '日' },
                    { value: timeLeft.hours, label: '時間' },
                    { value: timeLeft.minutes, label: '分' },
                    { value: timeLeft.seconds, label: '秒' },
                  ].map((item, idx) => (
                    <>
                      <div key={item.label} style={{ display:'flex', flexDirection:'column', alignItems:'center', minWidth:'34px' }}>
                        <span style={{
                          fontSize: '22px',
                          fontWeight: 700,
                          color: '#6a9a50',
                          lineHeight: 1,
                          fontFamily: "'Noto Sans JP', sans-serif",
                          letterSpacing: '-0.02em',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {String(item.value).padStart(2, '0')}
                        </span>
                        <span style={{
                          fontSize: '8px',
                          fontWeight: 600,
                          color: '#a8c890',
                          letterSpacing: '0.06em',
                          marginTop: '4px',
                          fontFamily: "'Noto Sans JP', sans-serif",
                        }}>{item.label}</span>
                      </div>
                      {idx < 3 && (
                        <span key={`sep-${idx}`} style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#c0dca8',
                          marginBottom: '10px',
                          lineHeight: 1,
                          fontFamily: "'Noto Sans JP', sans-serif",
                          userSelect: 'none',
                        }}>:</span>
                      )}
                    </>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="hero-animate-delay mb-5">
            <img
              alt="enme"
              className="h-14 w-auto mx-auto drop-shadow-lg"
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/4f1d6d7bad92488d1276d2951db9d591.png"
            />
          </div>

          <h1 className="hero-animate-delay2 serif-heading text-[2rem] font-light text-white leading-tight mb-3 tracking-wide" style={{ textShadow:'0 2px 16px rgba(0,0,0,0.3)' }}>
            自販機の水が、<br />
            <span
              className="font-bold italic"
              style={{
                color: '#d8f0b8',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(200,240,160,0.5)',
                textUnderlineOffset: '5px',
                textDecorationThickness: '1.5px',
              }}
            >5秒で「飲むサラダ」</span>に。
          </h1>

          <p className="hero-animate-delay3 text-sm text-white/80 leading-relaxed mb-8" style={{ textShadow:'0 1px 8px rgba(0,0,0,0.3)' }}>
            忙しい私を満たす、新発想のモバイル酵素習慣<br />
            <strong className="text-white font-bold">enme（エンミー）</strong>
          </p>

          <div className="relative w-full flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#f8faf5] pointer-events-none" style={{ top:'40%' }}></div>
            <img
              alt="enme VITAMIN C1000 ENZYME パッケージ"
              className="relative w-56 h-auto object-contain drop-shadow-2xl float-anim"
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/5e8cc1fa5b2f39b4086d33f30e06cb72.png"
            />
          </div>
        </div>

        <div className="relative z-10 max-w-sm mx-auto w-full px-6 pb-12">
          <div className="text-center mb-3">
            <span className="text-[11px] font-bold" style={{ color:'#f97316' }}>
              <i className="ri-building-2-line mr-1"></i>企業スポンサー枠、受付中！今すぐご確認を。
            </span>
          </div>
          <div className="cta-wrap w-full mb-4">
            <a
              href="https://camp-fire.jp/projects/872432/backers/new?id=1631430"
              target="_blank"
              rel="noopener noreferrer"
              className="line-btn"
            >
              <i className="ri-campfire-line text-xl"></i>
              <span>企業スポンサーとして支援する</span>
            </a>
          </div>
          <div className="glass-card rounded-2xl px-5 py-4 text-center">
            <p className="text-[10px] font-bold tracking-widest uppercase mb-1" style={{ color:'var(--green-deep)' }}>Now on CAMPFIRE</p>
            <p className="text-xs leading-relaxed" style={{ color:'var(--dark)' }}>クラウドファンディング公開中！<br />企業スポンサー枠も受付中。今すぐ支援内容をご確認ください。</p>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <section className="pt-16 pb-0 w-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #f2f7ec 0%, #e8f2de 100%)' }}>
        <div className="max-w-sm mx-auto px-6 fade-up">
          <span className="section-eyebrow">Your Concern</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-10 leading-snug" style={{ color:'var(--dark)' }}>
            こんな毎日、<br /><em className="font-bold not-italic" style={{ color:'var(--green-text)' }}>送っていませんか？</em>
          </h2>
          <p className="text-center text-xs mb-10 leading-relaxed" style={{ color:'#8a9a78' }}>「わかってるけど、できない」が続いてる…</p>

          <div className="relative" style={{ height: '280px' }}>
            <div className="absolute top-0 left-0 w-36 z-10">
              <div className="worry-card">お腹がゆるかったり<br />張ったり…安定しない</div>
            </div>
            <div className="absolute top-0 right-0 w-32 z-10">
              <div className="worry-card">肌がくすんで<br />透明感がない</div>
            </div>
            <div className="absolute inset-x-0 top-2 flex justify-center z-0">
              <img
                alt="お悩みの女性"
                className="w-40 h-auto object-contain object-top drop-shadow-lg"
                src="https://enme.saladfight.jp/wp-content/uploads/2025/04/mv-woman.png"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://public.readdy.ai/ai/img_res/f92a0a1f-380e-4133-80a6-1a7b19dae4d2.png';
                }}
              />
            </div>
            <div className="absolute left-0 z-10" style={{ top: '160px' }}>
              <div className="worry-card w-32">野菜、今週<br />食べてないかも…</div>
            </div>
            <div className="absolute right-0 z-10" style={{ top: '160px' }}>
              <div className="worry-card w-32">サプリ、買っても<br />飲み忘れる</div>
            </div>
          </div>

          <div className="mt-4 mb-0">
            <div className="glass-card rounded-3xl px-6 py-5 text-center">
              <p className="text-xs mb-1" style={{ color:'#8a9a78' }}>「気をつけなきゃ」と思いながらも</p>
              <p className="serif-heading text-lg font-bold leading-snug" style={{ color:'var(--dark)' }}>
                結局、今日も<span style={{ color:'var(--green-text)' }}>何もできなかった…</span>
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-sm mx-auto px-6 pt-6 pb-0 space-y-3 fade-up">
          {[
            { icon: 'ri-goblet-line', label: '食事の乱れ', text: 'コンビニ・外食が続いて、「今日も野菜ゼロだった」と夜に気づく。でも明日も忙しいし、どうせ変えられない気がする。' },
            { icon: 'ri-drop-line', label: '肌・体の変化', text: '以前より肌のくすみが気になる。ビタミンCが大事ってわかってるのに、レモンを毎日食べるなんて現実的じゃない。' },
            { icon: 'ri-capsule-line', label: 'サプリ疲れ', text: '「続けよう」と買ったサプリが、気づけば棚の奥に。錠剤を何粒も飲む習慣、正直しんどい。' },
            { icon: 'ri-mental-health-line', label: '腸の不調', text: 'なんとなくお腹が重い、スッキリしない日が続く。腸活って大事だとわかってるけど、何から始めればいいかわからない。' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-4 glass-card rounded-2xl px-5 py-4">
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background:'linear-gradient(135deg,#e8f5d8,#d8ecc8)' }}>
                  <i className={`${item.icon} text-xl`} style={{ color:'var(--green-deep)' }}></i>
                </div>
                <p className="text-[9px] font-bold text-center mt-1" style={{ color:'#8a9a78' }}>{item.label}</p>
              </div>
              <p className="text-sm leading-relaxed pt-1" style={{ color:'#3a4a28' }}>{item.text}</p>
            </div>
          ))}
        </div>

        <div className="w-full overflow-hidden leading-none mt-12" style={{ marginBottom:'-2px' }}>
          <svg viewBox="0 0 375 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width:'100%', height:'50px' }}>
            <path d="M0,25 C80,50 160,0 250,28 C310,45 350,10 375,22 L375,50 L0,50 Z" fill="#faf6e8" />
          </svg>
        </div>
      </section>

      {/* ===== SOLUTION ===== */}
      <section className="pt-12 pb-16 px-6 w-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #faf6e8 0%, #f5f0d8 100%)' }}>
        <div className="max-w-sm mx-auto fade-up">
          <span className="section-eyebrow" style={{ color:'#a08830' }}>Solution</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-2 leading-snug" style={{ color:'var(--dark)' }}>
            その悩み、enmeの<br />
            <em className="font-bold not-italic" style={{ color:'#8a7020' }}>「ちびだら飲み」</em>新習慣が<br />
            解決します。
          </h2>
          <p className="text-xs text-center mb-10" style={{ color:'#9a8840' }}>忙しいあなたに寄り添う、4つの特徴</p>

          {[
            {
              num:'01', title:'圧倒的な手軽さ', desc:'ペットボトルの水にサッと溶かすだけ。5秒で完成。',
              img:'https://readdy.ai/api/search-image?query=Soft%20Korean%20wellness%20aesthetic%20flat%20lay%20with%20fresh%20lemon%20slices%2C%20sage%20green%20botanical%20leaves%2C%20glass%20water%20bottle%2C%20pastel%20sage%20green%20and%20cream%20tones%2C%20elegant%20Korean%20beauty%20product%20photography%2C%20dreamy%20soft%20focus%20background%2C%20high-end%20editorial%20style%2C%20sage%20green%20and%20ivory%20palette%2C%20ultra%20clean%20minimalist%20composition%2C%20natural%20morning%20light&width=800&height=500&seq=sol1_green_v1&orientation=landscape',
              extra: null,
            },
            {
              num:'02', title:'タイムリリース処方', desc:'2時間かけてゆっくり飲み、点滴のようにビタミンCを補給。',
              img:'https://readdy.ai/api/search-image?query=Artistic%20close-up%20of%20vitamin%20C%20powder%20dissolving%20in%20crystal%20clear%20water%2C%20slow%20motion%20effect%2C%20soft%20sage%20green%20golden%20particles%20floating%2C%20luxury%20Korean%20beauty%20supplement%20photography%2C%20soft%20bokeh%20background%2C%20warm%20sage%20green%20and%20ivory%20tones%2C%20high-end%20editorial%20style%2C%20Japanese%20Korean%20minimalist%20aesthetic&width=800&height=500&seq=sol2_green_v1&orientation=landscape',
              extra: null,
            },
            {
              num:'03', title:'圧倒的な栄養', desc:'',
              img:'https://readdy.ai/api/search-image?query=Luxurious%20flat%20lay%20of%20fifty%20fresh%20lemons%20arranged%20artistically%20with%20probiotic%20capsules%20and%20green%20powder%20on%20soft%20sage%20green%20marble%2C%20editorial%20Korean%20beauty%20nutrition%20photography%2C%20vibrant%20yellow%20and%20sage%20green%2C%20sophisticated%20composition%2C%20warm%20natural%20light%2C%20high-end%20Japanese%20Korean%20wellness%20aesthetic&width=800&height=500&seq=sol3_green_v1&orientation=landscape',
              extra: ['ビタミンC 1,000mg','乳酸菌 100億個','食物繊維 2,400mg','酵素 100種類以上'],
            },
            {
              num:'04', title:'自然なダイエット習慣', desc:'イヌリンが満腹感をサポート。食事代わりにも。',
              img:'https://readdy.ai/api/search-image?query=Elegant%20slim%20Korean%20woman%20in%20soft%20white%20linen%20holding%20a%20clear%20glass%20bottle%20with%20green%20wellness%20drink%2C%20soft%20morning%20light%20studio%2C%20minimalist%20Korean%20beauty%20lifestyle%20photography%2C%20botanical%20leaves%20accent%2C%20sage%20green%20and%20yellow%20palette%2C%20editorial%20fashion%20wellness%20style%2C%20dreamy%20soft%20focus&width=800&height=500&seq=sol4_green_v1&orientation=landscape',
              extra: null,
            },
          ].map((f) => (
            <div key={f.num} className="mb-5 rounded-3xl overflow-hidden" style={{ background:'rgba(255,255,255,0.85)', border:'1px solid rgba(200,184,64,0.3)', boxShadow:'0 2px 16px rgba(180,160,80,0.12)' }}>
              <div className="relative">
                <img alt={f.title} className="w-full h-48 object-cover object-top" src={f.img} />
                <div className="absolute inset-0" style={{ background:'linear-gradient(to top, rgba(40,30,10,0.65) 0%, transparent 60%)' }}></div>
                <div className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-black" style={{ background:'linear-gradient(135deg,#c8b840,#e8d860)' }}>{f.num}</div>
                <div className="absolute bottom-4 left-5 right-5">
                  <h3 className="serif-heading text-base font-bold text-white mb-1">{f.title}</h3>
                  {f.desc && <p className="text-xs text-white/80 leading-relaxed">{f.desc}</p>}
                  {f.extra && (
                    <div className="grid grid-cols-2 gap-1.5 mt-1">
                      {f.extra.map((n) => (
                        <div key={n} className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2 py-1.5">
                          <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background:'#f0e080' }}></span>
                          <span className="text-[10px] text-white font-medium">{n}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {f.num === '04' && (
                <div className="px-5 py-5">
                  <div className="space-y-2">
                    {[
                      { icon: 'ri-expand-up-down-line', title: '胃・腸で膨らみ、満腹感を持続', sub: '食べ過ぎ・間食を自然にブロック' },
                      { icon: 'ri-restaurant-line', title: '忙しい朝・ランチの置き換えに', sub: '水に溶かすだけで手軽に栄養補給' },
                      { icon: 'ri-scales-3-line', title: '無理なく続けられるダイエット', sub: '我慢ゼロ、習慣化しやすい新アプローチ' },
                    ].map((d) => (
                      <div key={d.title} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background:'rgba(200,184,64,0.12)', border:'1px solid rgba(200,184,64,0.25)' }}>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background:'rgba(200,184,64,0.18)' }}>
                          <i className={`${d.icon} text-base`} style={{ color:'#a89830' }}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold leading-tight" style={{ color:'var(--dark)' }}>{d.title}</p>
                          <p className="text-[10px] leading-tight" style={{ color:'#9a8840' }}>{d.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>



      {/* ===== MID CTA ===== */}
      <section className="py-12 px-6 w-full" style={{ background:'linear-gradient(135deg,#8aba6a 0%,#6a9a50 100%)' }}>
        <div className="max-w-sm mx-auto fade-up text-center">
          <span className="section-eyebrow" style={{ color:'#e8ffd8' }}>Corporate Sponsor</span>
          <p className="serif-heading text-xl font-light mb-1 leading-snug text-white">
            enmeの挑戦に、<br />
            <em className="font-bold not-italic" style={{ color:'#e8ffd8' }}>参加しませんか？</em>
          </p>
          <p className="text-xs mb-4 leading-relaxed" style={{ color:'rgba(255,255,255,0.8)' }}>
            スポンサー枠はCAMPFIREにて受付中。<br />ブランドとともに、新しい健康文化をつくりましょう。
          </p>
          <div className="mb-4">
            <span className="text-[11px] font-bold" style={{ color:'#f9f0c0' }}>
              <i className="ri-building-2-line mr-1"></i>企業スポンサー枠、受付中。詳細はCAMPFIREページをご確認ください。
            </span>
          </div>
          <div className="cta-wrap w-full mb-3">
            <a
              href="https://camp-fire.jp/projects/872432/backers/new?id=1631430"
              target="_blank"
              rel="noopener noreferrer"
              className="line-btn"
            >
              <i className="ri-campfire-line text-xl"></i>
              <span>企業スポンサーとして支援する</span>
            </a>
          </div>
          <p className="text-[10px]" style={{ color:'rgba(255,255,255,0.65)' }}>※ スポンサー枠には数量制限があります。お早めにご検討ください。</p>
        </div>
      </section>

      {/* ===== INGREDIENTS ===== */}
      <section className="pt-12 pb-16 px-6 w-full" style={{ background: 'linear-gradient(180deg, #f2f7ec 0%, #e8f2de 100%)' }}>
        <div className="max-w-sm mx-auto fade-up">
          <span className="section-eyebrow">Ingredients</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-2 leading-snug" style={{ color:'var(--dark)' }}>
            レモン50個分のビタミンCを、<br /><em className="font-bold not-italic" style={{ color:'var(--green-text)' }}>この1包に凝縮。</em>
          </h2>
          <p className="text-xs text-center mb-8 leading-relaxed" style={{ color:'#8a9a78' }}>まるで「飲む点滴」。必要な栄養素を、美味しくチャージ。</p>

          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute -inset-8 rounded-full blur-3xl" style={{ background:'radial-gradient(circle, rgba(180,220,140,0.3) 0%, transparent 70%)' }}></div>
              <img
                alt="enme パッケージ"
                className="relative w-52 h-auto object-contain drop-shadow-2xl mx-auto float-anim"
                src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/5e8cc1fa5b2f39b4086d33f30e06cb72.png"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { icon:'ri-sun-line', label:'ビタミンC', value:'1,000', unit:'mg', sub:'レモン50個分', from:'#f5faf0', to:'#e8f5d8', border:'rgba(138,160,100,0.4)', iconColor:'#7aaa5a', textColor:'#4a7a2a' },
              { icon:'ri-leaf-line', label:'食物繊維\nイヌリン', value:'2,400', unit:'mg', sub:'水溶性', from:'#fafaf0', to:'#f5f2d8', border:'rgba(200,184,64,0.4)', iconColor:'#a89830', textColor:'#7a6a10' },
              { icon:'ri-heart-pulse-line', label:'乳酸菌', value:'100億', unit:'個', sub:'1包あたり', from:'#f0faf5', to:'#d8f0e8', border:'rgba(100,140,100,0.4)', iconColor:'#50a878', textColor:'#2a7050' },
            ].map((n) => (
              <div key={n.label} className="rounded-3xl px-3 py-4 text-center shadow-sm relative overflow-hidden" style={{ background:`linear-gradient(135deg,${n.from},${n.to})`, border:`1.5px solid ${n.border}` }}>
                <div className="w-8 h-8 flex items-center justify-center rounded-full mx-auto mb-2 bg-white/70">
                  <i className={`${n.icon} text-base`} style={{ color:n.iconColor }}></i>
                </div>
                <p className="text-[9px] font-bold mb-0.5 leading-tight whitespace-pre-line" style={{ color:n.textColor }}>{n.label}</p>
                <p className="text-xl font-black leading-none" style={{ color:n.iconColor }}>{n.value}</p>
                <p className="text-[9px] font-bold" style={{ color:n.iconColor }}>{n.unit}</p>
                <p className="text-[8px] mt-1" style={{ color:n.textColor }}>{n.sub}</p>
              </div>
            ))}
          </div>

          <div className="glass-card rounded-2xl px-5 py-4 flex items-center gap-4 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background:'linear-gradient(135deg,#e8f5d8,#d8ecc8)' }}>
              <i className="ri-flask-line text-lg" style={{ color:'var(--green-deep)' }}></i>
            </div>
            <p className="text-xs leading-relaxed" style={{ color:'#3a4a28' }}>
              <strong style={{ color:'var(--dark)' }}>国産野菜・果実100種類以上</strong>の発酵エキスを凝縮。<br />まるで「飲む点滴」の栄養密度。
            </p>
          </div>

          {/* Price Card — 明るいグリーン系 */}
          <div className="rounded-2xl px-5 py-5 text-center" style={{ background:'linear-gradient(135deg,#e8f5d8,#d4ecb8)', border:'1.5px solid rgba(122,170,90,0.4)' }}>
            <p className="text-[10px] tracking-widest font-bold uppercase mb-1" style={{ color:'var(--green-deep)' }}>Price</p>
            <p className="text-xs mb-1" style={{ color:'#5a7a40' }}>クラウドファンディング最安値価格</p>
            <div className="flex items-end justify-center gap-1 mb-1">
              <span className="text-3xl font-black" style={{ color:'var(--dark)' }}>約250円</span>
              <span className="text-sm mb-1" style={{ color:'#8a9a78' }}>〜 / 1包</span>
            </div>
            <p className="text-[10px] font-bold" style={{ color:'var(--green-text)' }}>※超早割適用時の最安値価格</p>
          </div>
        </div>
      </section>

      {/* ===== TRUST ===== */}
      <div className="w-full overflow-hidden leading-none" style={{ background:'linear-gradient(180deg,#f2f7ec,#e8f2de)', marginBottom:'-2px' }}>
        <svg viewBox="0 0 375 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width:'100%', height:'50px' }}>
          <path d="M0,15 C80,45 180,0 280,30 C330,45 360,15 375,22 L375,50 L0,50 Z" fill="#faf6e8" />
        </svg>
      </div>
      <section className="pt-12 pb-16 px-6 w-full overflow-hidden" style={{ background:'linear-gradient(180deg,#faf6e8 0%,#f5f0d8 100%)' }}>
        <div className="max-w-sm mx-auto fade-up">
          <span className="section-eyebrow" style={{ color:'#a08830' }}>Trust &amp; Authority</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-10 leading-snug" style={{ color:'var(--dark)' }}>
            老舗メーカーが生んだ<br /><em className="font-bold not-italic" style={{ color:'#8a7020' }}>確かな品質</em>
          </h2>

          <div className="rounded-3xl overflow-hidden shadow-md" style={{ background:'rgba(255,255,255,0.9)', border:'1px solid rgba(200,184,64,0.3)' }}>
            <div className="relative">
              <img
                alt="製造工場"
                className="w-full h-48 object-cover object-top"
                src="https://readdy.ai/api/search-image?query=Traditional%20Japanese%20artisan%20fermentation%20workshop%20with%20wooden%20barrels%20and%20modern%20stainless%20equipment%2C%20warm%20amber%20golden%20lighting%2C%20heritage%20craftsmanship%20meets%20contemporary%20manufacturing%2C%20editorial%20photography%2C%20sophisticated%20trustworthy%20atmosphere%2C%20muted%20earth%20tones%2C%20elegant%20and%20refined%20aesthetic&width=800&height=500&seq=factory_green_v1&orientation=landscape"
              />
              <div className="absolute inset-0" style={{ background:'linear-gradient(to top, rgba(40,30,10,0.6) 0%, transparent 50%)' }}></div>
              <div className="absolute bottom-4 left-5">
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-1" style={{ background:'rgba(168,152,48,0.9)' }}>
                  <i className="ri-building-line text-white text-xs"></i>
                  <span className="text-[10px] font-bold text-white tracking-wide uppercase">老舗メーカー</span>
                </div>
                <p className="text-white font-black text-sm">創業70余年の発酵技術</p>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs font-medium mb-2" style={{ color:'#8a7020' }}>日本で初めて酵素飲料を開発</p>
              <p className="text-xs leading-relaxed" style={{ color:'#3a3a28' }}>伝統の発酵技術で、酵素独特の匂いを抑えた<strong style={{ color:'var(--dark)' }}>美味しいレモン風味</strong>を実現。毎日続けられる味わいにこだわりました。</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BRAND STORY ===== */}
      <div className="w-full overflow-hidden leading-none" style={{ background:'linear-gradient(180deg,#faf6e8,#f5f0d8)', marginBottom:'-2px' }}>
        <svg viewBox="0 0 375 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width:'100%', height:'50px' }}>
          <path d="M0,25 C90,50 180,0 280,28 C330,42 360,12 375,20 L375,50 L0,50 Z" fill="#8aba6a" />
        </svg>
      </div>
      <section className="pt-12 pb-16 px-6 w-full" style={{ background:'linear-gradient(180deg,#eef6e6 0%,#e4f0d8 100%)' }}>
        <div className="max-w-sm mx-auto fade-up">
          <span className="section-eyebrow">Brand Story</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-2 leading-snug" style={{ color:'var(--dark)' }}>
            「忙しい人を、<br /><em className="font-bold not-italic" style={{ color:'var(--green-text)' }}>サラダで満たしたい。</em>」
          </h2>
          <p className="text-xs text-center mb-8 leading-relaxed" style={{ color:'#8a9a78' }}>大阪発・サラダ専門店が生んだ、新しい栄養習慣の形。</p>

          <div className="relative rounded-3xl overflow-hidden shadow-xl mb-6">
            <img alt="大阪のサラダ専門店" className="w-full h-64 object-cover object-top" src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/d696728a20f92126f112d40f8b1468b7.jpeg" />
            <div className="absolute inset-0" style={{ background:'linear-gradient(to top, rgba(30,50,20,0.5) 0%, transparent 60%)' }}></div>
            <div className="absolute bottom-4 left-5 flex items-center gap-2">
              <i className="ri-map-pin-line text-sm" style={{ color:'#c8e8a0' }}></i>
              <span className="text-xs font-bold text-white tracking-wide">大阪発 — サラダ専門店</span>
            </div>
          </div>

          <div className="space-y-5 mb-8">
            {[
              { icon:'ri-store-2-line', title:'大阪のサラダ専門店から生まれた', text:'「サラダで満腹への挑戦」をコンセプトに、忙しいビジネスパーソンや健康意識の高い方々に愛されてきた大阪のサラダ専門店。毎日お店に来られない方にも、同じ想いを届けたい——そんな願いからenmeは誕生しました。' },
              { icon:'ri-user-heart-line', title:'「Enrich Me」—— 私を豊かに', text:'enmeの名前は「Enrich Me(私を豊かにする)」から。忙しくて食事を後回しにしがちな現代人が、移動中でも・仕事中でも、手軽に体の内側から満たされる——そんな新しい栄養習慣を提案します。' },
              { icon:'ri-seedling-line', title:'お店の「生きた栄養」を、1包に', text:'野菜・果実・発酵の力を凝縮したenmeは、まさにサラダ専門店のノウハウを詰め込んだ1包。「飲むサラダ」として、あなたの毎日に寄り添います。' },
            ].map((s) => (
              <div key={s.title} className="flex gap-4">
                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl mt-0.5" style={{ background:'linear-gradient(135deg,#e8f5d8,#d8ecc8)' }}>
                  <i className={`${s.icon} text-base`} style={{ color:'var(--green-deep)' }}></i>
                </div>
                <div>
                  <p className="serif-heading text-sm font-bold mb-1" style={{ color:'var(--dark)' }}>{s.title}</p>
                  <p className="text-xs leading-relaxed" style={{ color:'#4a5a38' }}>{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative rounded-3xl px-6 py-6 shadow-sm" style={{ background:'rgba(255,255,255,0.85)', border:'1px solid rgba(200,184,64,0.3)' }}>
            <div className="absolute -top-3 left-6 w-7 h-7 flex items-center justify-center rounded-full shadow" style={{ background:'linear-gradient(135deg,var(--green-deep),var(--green))' }}>
              <i className="ri-double-quotes-l text-white text-xs"></i>
            </div>
            <p className="serif-heading text-sm font-light leading-relaxed italic mt-2" style={{ color:'#3a3a10' }}>
              「お店に来られない日も、あなたの体を満たしたい。<br />それがenmeを作った、私たちの想いです。」
            </p>
            <p className="text-[10px] font-bold mt-3 tracking-wide" style={{ color:'var(--green-deep)' }}>— enme 開発チーム / 大阪サラダ専門店</p>
          </div>
        </div>
      </section>

      {/* ===== NUMBERS ===== */}
      <div className="w-full overflow-hidden leading-none" style={{ background:'linear-gradient(180deg,#eef6e6,#e4f0d8)', marginBottom:'-2px' }}>
        <svg viewBox="0 0 375 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width:'100%', height:'50px' }}>
          <path d="M0,30 C80,5 160,45 250,18 C310,2 350,35 375,20 L375,50 L0,50 Z" fill="#8aba6a" />
        </svg>
      </div>
      <section className="pt-12 pb-16 px-6 relative overflow-hidden w-full" style={{ background:'linear-gradient(135deg,#8aba6a,#6a9a50)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full" style={{ background:'rgba(255,255,255,0.08)' }}></div>
          <div className="absolute -bottom-20 -right-10 w-80 h-80 rounded-full" style={{ background:'rgba(255,255,255,0.08)' }}></div>
        </div>
        <div className="max-w-sm mx-auto relative z-10 fade-up">
          <span className="section-eyebrow" style={{ color:'#e8ffd8' }}>Numbers</span>
          <h2 className="serif-heading text-xl font-light text-center mb-8 leading-snug text-white">
            数字で見る、<em className="font-bold not-italic" style={{ color:'#e8ffd8' }}>enmeの実力</em>
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon:'ri-sun-line', value:'1,000', unit:'mg', label:'ビタミンC', sub:'レモン50個分', accent:'#d8f0a0', bg:'rgba(255,255,255,0.15)', border:'rgba(216,240,160,0.35)' },
              { icon:'ri-heart-pulse-line', value:'100億', unit:'個', label:'乳酸菌', sub:'1包あたり', accent:'#b8f0c8', bg:'rgba(255,255,255,0.15)', border:'rgba(184,240,200,0.35)' },
              { icon:'ri-leaf-line', value:'100', unit:'+', label:'種類の酵素', sub:'凝縮配合', accent:'#e0f0a0', bg:'rgba(255,255,255,0.15)', border:'rgba(224,240,160,0.35)' },
              { icon:'ri-award-line', value:'70', unit:'年+', label:'発酵の歴史', sub:'老舗メーカー', accent:'#c8f0b0', bg:'rgba(255,255,255,0.15)', border:'rgba(200,240,176,0.35)' },
            ].map((n) => (
              <div key={n.label} className="relative overflow-hidden rounded-3xl px-4 py-5" style={{ background:n.bg, border:`1px solid ${n.border}` }}>
                <div className="absolute -bottom-4 -right-4">
                  <i className={`${n.icon} text-7xl`} style={{ color:n.accent, opacity:0.18 }}></i>
                </div>
                <div className="w-9 h-9 flex items-center justify-center rounded-xl mb-3" style={{ background:'rgba(255,255,255,0.2)' }}>
                  <i className={`${n.icon} text-lg`} style={{ color:n.accent }}></i>
                </div>
                <p className="text-2xl font-black leading-none mb-0.5" style={{ color:n.accent }}>{n.value}<span className="text-sm font-bold">{n.unit}</span></p>
                <p className="text-xs font-bold mb-0.5 text-white">{n.label}</p>
                <p className="text-[10px]" style={{ color:'rgba(255,255,255,0.7)' }}>{n.sub}</p>
                <div className="mt-3 rounded-full h-0.5 overflow-hidden" style={{ background:'rgba(255,255,255,0.2)' }}>
                  <div className="h-full rounded-full w-full" style={{ background:`linear-gradient(90deg,${n.accent},${n.accent}66)` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <div className="w-full overflow-hidden leading-none" style={{ background:'linear-gradient(135deg,#8aba6a,#6a9a50)', marginBottom:'-2px' }}>
        <svg viewBox="0 0 375 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width:'100%', height:'50px' }}>
          <path d="M0,20 C70,45 160,0 260,28 C310,40 355,12 375,20 L375,50 L0,50 Z" fill="#faf6e8" />
        </svg>
      </div>
      <section id="cta" className="pt-12 pb-16 px-6 w-full" style={{ background:'linear-gradient(180deg,#faf6e8 0%,#f5f0d8 100%)' }}>
        <div className="max-w-sm mx-auto fade-up">
          <span className="section-eyebrow">Corporate Sponsor</span>
          <h2 className="serif-heading text-2xl font-light text-center mb-2 leading-snug" style={{ color:'var(--dark)' }}>
            enmeの挑戦に、<br />企業として<em className="font-bold not-italic" style={{ color:'#8a7020' }}>参加しませんか？</em>
          </h2>
          <p className="text-xs text-center mb-8 leading-relaxed" style={{ color:'#9a8840' }}>
            クラウドファンディング公開中！<br />
            <strong style={{ color:'#8a7020' }}>企業スポンサー枠</strong>にてパートナー様を募集しています。
          </p>

          <div className="rounded-3xl px-5 py-5 mb-6" style={{ background:'rgba(255,255,255,0.85)', border:'1px solid rgba(200,184,64,0.3)' }}>
            <p className="text-xs font-black mb-4 text-center tracking-wide" style={{ color:'var(--dark)' }}>企業スポンサーとして支援いただける方へ</p>
            <ul className="space-y-3">
              {[
                'CAMPFIREプロジェクトページへの企業名・ロゴ掲載',
                'enme公式SNSでのスポンサー企業紹介',
                '新しい健康文化をともに創るブランドパートナーシップ',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ background:'linear-gradient(135deg,#c8b840,#e8d860)' }}>
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm" style={{ color:'#3a3a10' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center mb-5">
            <span className="shimmer-text text-xs font-black tracking-widest">＼ 企業スポンサー枠、絶賛受付中！ ／</span>
          </div>
          <div className="text-center mb-3">
            <span className="text-[11px] font-bold" style={{ color:'#f97316' }}>
              <i className="ri-building-2-line mr-1"></i>スポンサー枠には数量制限があります。お早めにご検討ください。
            </span>
          </div>
          <div className="cta-wrap w-full mb-4">
            <a
              href="https://camp-fire.jp/projects/872432/backers/new?id=1631430"
              target="_blank"
              rel="noopener noreferrer"
              className="line-btn"
            >
              <i className="ri-campfire-line text-xl"></i>
              <span>企業スポンサーとして支援する</span>
            </a>
          </div>

          <p className="text-[10px] text-center mt-2 leading-relaxed" style={{ color:'#9a8840' }}>
            ※ スポンサー枠は数量限定・なくなり次第終了です。お早めにどうぞ。
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t py-10 px-6 w-full" style={{ borderColor:'rgba(138,170,90,0.3)', background:'linear-gradient(180deg,#8aba6a 0%,#6a9a50 100%)' }}>
        <div className="max-w-sm mx-auto text-center">
          <img
            alt="enme"
            className="h-10 w-auto mx-auto mb-3 opacity-80"
            src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/4f1d6d7bad92488d1276d2951db9d591.png"
          />
          <p className="text-xs leading-relaxed mb-4" style={{ color:'rgba(255,255,255,0.75)' }}>
            忙しい現代人のための、新しい酵素習慣。<br />enme（エンミー）
          </p>
          <p className="text-xs" style={{ color:'rgba(255,255,255,0.5)' }}>© 2025 enme. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
